import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useToast,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
  InputGroup,
  InputLeftElement
} from '@chakra-ui/react';
import { SearchIcon, AddIcon, EditIcon, DeleteIcon, ChevronDownIcon } from '@chakra-ui/icons';
import apiService from '../../services/ApiService';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    role: 'user',
    password: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const toast = useToast();

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, roleFilter]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setError('');

      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter && { role: roleFilter })
      });

      const response = await apiService({
        method: 'GET',
        url: `/users?${params}`
      });

      setUsers(response.data.users);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('ไม่สามารถโหลดข้อมูลผู้ใช้ได้');
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถโหลดข้อมูลผู้ใช้ได้',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleRoleFilter = (e) => {
    setRoleFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleCreateUser = () => {
    setFormData({
      username: '',
      email: '',
      full_name: '',
      role: 'user',
      password: ''
    });
    setFormErrors({});
    onOpen();
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      password: ''
    });
    setFormErrors({});
    onEditOpen();
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    onDeleteOpen();
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.username.trim()) {
      errors.username = 'กรุณากรอกชื่อผู้ใช้';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'กรุณากรอกอีเมล';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'รูปแบบอีเมลไม่ถูกต้อง';
    }
    
    if (!formData.full_name.trim()) {
      errors.full_name = 'กรุณากรอกชื่อ-นามสกุล';
    }
    
    if (!selectedUser && !formData.password.trim()) {
      errors.password = 'กรุณากรอกรหัสผ่าน';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      
      const submitData = { ...formData };
      if (selectedUser && !submitData.password) {
        delete submitData.password;
      }

      if (selectedUser) {
        // Update user
        await apiService({
          method: 'PUT',
          url: `/users/${selectedUser.id}`,
          data: submitData
        });
        
        toast({
          title: 'อัปเดตผู้ใช้สำเร็จ',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onEditClose();
      } else {
        // Create user
        await apiService({
          method: 'POST',
          url: '/users',
          data: submitData
        });
        
        toast({
          title: 'สร้างผู้ใช้สำเร็จ',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onClose();
      }
      
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: error.message || 'ไม่สามารถบันทึกข้อมูลได้',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await apiService({
        method: 'DELETE',
        url: `/users/${selectedUser.id}`
      });
      
      toast({
        title: 'ลบผู้ใช้สำเร็จ',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      onDeleteClose();
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: error.message || 'ไม่สามารถลบผู้ใช้ได้',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const toggleUserStatus = async (user) => {
    try {
      await apiService({
        method: 'PUT',
        url: `/users/${user.id}`,
        data: { is_active: !user.is_active }
      });
      
      toast({
        title: 'อัปเดตสถานะสำเร็จ',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถอัปเดตสถานะได้',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (isLoading) {
    return (
      <Center h="400px">
        <VStack>
          <Spinner size="xl" color="blue.500" />
          <Text>กำลังโหลดข้อมูล...</Text>
        </VStack>
      </Center>
    );
  }

  if (error) {
    return (
      <Alert status="error" rounded="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Text fontSize="2xl" fontWeight="bold">จัดการผู้ใช้</Text>
        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={handleCreateUser}>
          เพิ่มผู้ใช้
        </Button>
      </Flex>

      {/* Filters */}
      <Box bg="white" p={4} rounded="lg" shadow="sm" mb={6}>
        <HStack spacing={4}>
          <InputGroup maxW="300px">
            <InputLeftElement>
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="ค้นหาผู้ใช้..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </InputGroup>
          
          <Select
            placeholder="เลือกบทบาท"
            value={roleFilter}
            onChange={handleRoleFilter}
            maxW="200px"
          >
            <option value="admin">ผู้ดูแล</option>
            <option value="user">ผู้ใช้</option>
          </Select>
        </HStack>
      </Box>

      {/* Users Table */}
      <Box bg="white" rounded="lg" shadow="sm" overflow="hidden">
        <Table>
          <Thead bg="gray.50">
            <Tr>
              <Th>ชื่อผู้ใช้</Th>
              <Th>อีเมล</Th>
              <Th>ชื่อ-นามสกุล</Th>
              <Th>บทบาท</Th>
              <Th>สถานะ</Th>
              <Th>วันที่สร้าง</Th>
              <Th>จัดการ</Th>
            </Tr>
          </Thead>
          <Tbody>
            {users.map((user) => (
              <Tr key={user.id}>
                <Td>
                  <Text fontWeight="medium">{user.username}</Text>
                </Td>
                <Td>{user.email}</Td>
                <Td>{user.full_name}</Td>
                <Td>
                  <Badge colorScheme={user.role === 'admin' ? 'red' : 'blue'}>
                    {user.role === 'admin' ? 'ผู้ดูแล' : 'ผู้ใช้'}
                  </Badge>
                </Td>
                <Td>
                  <Badge 
                    colorScheme={user.is_active ? 'green' : 'red'}
                    cursor="pointer"
                    onClick={() => toggleUserStatus(user)}
                  >
                    {user.is_active ? 'ใช้งาน' : 'ไม่ใช้งาน'}
                  </Badge>
                </Td>
                <Td>
                  <Text fontSize="sm" color="gray.500">
                    {new Date(user.created_at).toLocaleDateString('th-TH')}
                  </Text>
                </Td>
                <Td>
                  <Menu>
                    <MenuButton as={IconButton} icon={<ChevronDownIcon />} variant="ghost" size="sm" />
                    <MenuList>
                      <MenuItem icon={<EditIcon />} onClick={() => handleEditUser(user)}>
                        แก้ไข
                      </MenuItem>
                      <MenuItem icon={<DeleteIcon />} onClick={() => handleDeleteUser(user)} color="red.500">
                        ลบ
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <HStack justify="center" mt={6}>
          <Button
            isDisabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            ก่อนหน้า
          </Button>
          <Text>
            หน้า {currentPage} จาก {totalPages}
          </Text>
          <Button
            isDisabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            ถัดไป
          </Button>
        </HStack>
      )}

      {/* Create/Edit User Modal */}
      <Modal isOpen={isOpen || isEditOpen} onClose={selectedUser ? onEditClose : onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedUser ? 'แก้ไขผู้ใช้' : 'เพิ่มผู้ใช้ใหม่'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <form onSubmit={handleSubmit}>
              <VStack spacing={4}>
                <FormControl isInvalid={formErrors.username}>
                  <FormLabel>ชื่อผู้ใช้</FormLabel>
                  <Input
                    name="username"
                    value={formData.username}
                    onChange={handleFormChange}
                    placeholder="กรอกชื่อผู้ใช้"
                  />
                  <FormErrorMessage>{formErrors.username}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={formErrors.email}>
                  <FormLabel>อีเมล</FormLabel>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    placeholder="กรอกอีเมล"
                  />
                  <FormErrorMessage>{formErrors.email}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={formErrors.full_name}>
                  <FormLabel>ชื่อ-นามสกุล</FormLabel>
                  <Input
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleFormChange}
                    placeholder="กรอกชื่อ-นามสกุล"
                  />
                  <FormErrorMessage>{formErrors.full_name}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>บทบาท</FormLabel>
                  <Select
                    name="role"
                    value={formData.role}
                    onChange={handleFormChange}
                  >
                    <option value="user">ผู้ใช้</option>
                    <option value="admin">ผู้ดูแล</option>
                  </Select>
                </FormControl>

                <FormControl isInvalid={formErrors.password}>
                  <FormLabel>
                    รหัสผ่าน {selectedUser && '(เว้นว่างไว้หากไม่ต้องการเปลี่ยน)'}
                  </FormLabel>
                  <Input
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleFormChange}
                    placeholder="กรอกรหัสผ่าน"
                  />
                  <FormErrorMessage>{formErrors.password}</FormErrorMessage>
                </FormControl>

                <HStack w="100%" justify="flex-end">
                  <Button variant="ghost" onClick={selectedUser ? onEditClose : onClose}>
                    ยกเลิก
                  </Button>
                  <Button type="submit" colorScheme="blue" isLoading={isSubmitting}>
                    {selectedUser ? 'อัปเดต' : 'สร้าง'}
                  </Button>
                </HStack>
              </VStack>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>ยืนยันการลบ</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text>
              คุณแน่ใจหรือไม่ที่จะลบผู้ใช้ <strong>{selectedUser?.full_name}</strong>?
              การดำเนินการนี้ไม่สามารถย้อนกลับได้
            </Text>
            <HStack mt={4} justify="flex-end">
              <Button variant="ghost" onClick={onDeleteClose}>
                ยกเลิก
              </Button>
              <Button colorScheme="red" onClick={handleDelete}>
                ลบ
              </Button>
            </HStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default UserManagement;


